import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../utils/api";
import {
  shouldShowAnnouncementPopup,
  recordAnnouncementDismissed,
  recordAnnouncementShown,
  selectNextPopup,
  setLastShownPopupId,
} from "../utils/announcementPopupStorage";
import "./AnnouncementPopup.css";

function MegaphoneIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1.3.5 2.6 1.5 3.5.8.8 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6" />
      <path d="M10 22h4" />
    </svg>
  );
}

function RocketIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  );
}

function SunriseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 2v8" />
      <path d="m4.93 10.93 1.41 1.41" />
      <path d="M2 18h20" />
      <path d="M20 18a8 8 0 0 0-16 0" />
      <path d="m19.07 10.93-1.41 1.41" />
      <path d="M22 22H2" />
      <path d="m8 6 4-4 4 4" />
    </svg>
  );
}

function CategoryIcon({ type }) {
  switch (type) {
    case "fact":
      return <LightbulbIcon />;
    case "spotlight":
      return <RocketIcon />;
    case "snapshot":
      return <SunriseIcon />;
    default:
      return <MegaphoneIcon />;
  }
}

// Exact official social media links
const DEFAULT_SOCIAL_SLOTS = [
  {
    name: "YouTube",
    href: "https://www.youtube.com/@enfo.aipakistan/videos",
    bgClass: "social-bg-youtube",
    iconSvg: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="#FFFFFF">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/enfopk/",
    bgClass: "social-bg-facebook",
    iconSvg: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="#FFFFFF">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/company/72250475/admin/page-posts/published/",
    bgClass: "social-bg-linkedin",
    iconSvg: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="#FFFFFF">
        <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
      </svg>
    ),
  },
];

/**
 * Extracts snapshot numbers from popup object or plain text description fallback.
 */
function getSnapshotStats(popup) {
  if (popup?.stats && typeof popup.stats === "object") return popup.stats;
  const desc = popup?.description || "";
  if (!desc.includes("Energy Consumed") && !desc.includes("Max Demand")) return null;

  const kwhMatch = desc.match(/Energy Consumed:\s*([\d,]+(?:\.\d+)?)/i);
  const pkrMatch = desc.match(/~PKR\s*([\d,]+)/i);
  const mdiMatch = desc.match(/Max Demand \((?:MDI|Peak)\):\s*([\d,]+(?:\.\d+)?)/i);
  const pfMatch = desc.match(/Avg Power Factor:\s*([\d\.]+)\s*\((.*?)\)/i);

  if (kwhMatch || mdiMatch) {
    return {
      energy_kwh: kwhMatch ? parseFloat(kwhMatch[1].replace(/,/g, "")) : 4250,
      estimated_pkr: pkrMatch ? parseInt(pkrMatch[1].replace(/,/g, ""), 10) : 170000,
      max_demand_kw: mdiMatch ? parseFloat(mdiMatch[1].replace(/,/g, "")) : 310,
      avg_power_factor: pfMatch ? parseFloat(pfMatch[1]) : 0.92,
    };
  }
  return null;
}

function getCategoryLabel(popup, isSnapshotCard) {
  if (popup?.category_label) return popup.category_label;
  if (isSnapshotCard) return "🌅 Daily Energy Snapshot";
  if (popup?.popup_type === "fact") {
    if (popup.heading?.includes("Tariff")) return "⚡ Cost Saving Tip";
    if (popup.heading?.includes("Air")) return "🔧 Maintenance Tip";
    if (popup.heading?.includes("Transformer")) return "🔌 Energy Saving Tip";
    return "💡 Efficiency Tip";
  }
  if (popup?.popup_type === "spotlight") {
    if (popup.heading?.includes("Resolution")) return "📊 enfo Pro Tip";
    return "🚀 enfo Feature Spotlight";
  }
  return "📢 Official Announcement";
}

/**
 * After login only: at most one announcement/fallback popup per 24h (localStorage).
 */
export default function AnnouncementPopup() {
  const location = useLocation();
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);

  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const username =
    typeof window !== "undefined" ? localStorage.getItem("username") || "" : "";
  const onPrivateRoute =
    location.pathname !== "/login" && token && token !== "undefined";

  const dismiss = useCallback(() => {
    recordAnnouncementDismissed();
    setVisible(false);
    setPopup(null);
  }, []);

  useEffect(() => {
    if (!onPrivateRoute) {
      setPopup(null);
      setVisible(false);
      return;
    }

    let cancelled = false;

    async function load() {
      if (!shouldShowAnnouncementPopup()) {
        if (process.env.NODE_ENV === "development") {
          console.debug(
            "[AnnouncementPopup] Skipped: 24h cooldown (clear localStorage keys enviz_popup_* to retest)"
          );
        }
        return;
      }

      try {
        const { data } = await api.get("/popups/active", {
          params: { username },
          timeout: 15000,
        });
        const list = Array.isArray(data) ? data : data?.popups || [];
        if (!list.length) {
          if (process.env.NODE_ENV === "development") {
            console.debug("[AnnouncementPopup] API returned no popups.");
          }
          if (cancelled) return;
          return;
        }
        if (cancelled) return;

        const chosen = selectNextPopup(list);
        if (!chosen || cancelled) return;

        recordAnnouncementShown();
        setLastShownPopupId(chosen.id);
        setPopup(chosen);
        setVisible(true);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.debug("[AnnouncementPopup] No popup or API error:", e?.message);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [onPrivateRoute, username]);

  useEffect(() => {
    if (!visible) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  if (!visible || !popup) return null;

  const type = popup.popup_type || "announcement";
  const bg =
    popup.background_image ||
    popup.backgroundImage ||
    (popup.background_image_url ? popup.background_image_url : "");

  const i1 = popup.icon1_image ?? popup.icon1Image;
  const i2 = popup.icon2_image ?? popup.icon2Image;
  const i3 = popup.icon3_image ?? popup.icon3Image;
  const l1 = popup.icon1_link ?? popup.icon1Link ?? "";
  const l2 = popup.icon2_link ?? popup.icon2Link ?? "";
  const l3 = popup.icon3_link ?? popup.icon3Link ?? "";

  const socialSlots = [
    { img: i1, href: l1, default: DEFAULT_SOCIAL_SLOTS[0] },
    { img: i2, href: l2, default: DEFAULT_SOCIAL_SLOTS[1] },
    { img: i3, href: l3, default: DEFAULT_SOCIAL_SLOTS[2] },
  ];

  const parsedStats = getSnapshotStats(popup);
  const isSnapshotCard =
    type === "snapshot" ||
    !!parsedStats ||
    popup.heading?.includes("Yesterday's Plant Summary") ||
    popup.description?.includes("Energy Consumed");

  return (
    <div
      className="announcement-overlay"
      role="presentation"
      onClick={dismiss}
    >
      <div
        className={`announcement-dialog popup-type-${type}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-heading"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`announcement-bg ${!bg ? `fallback-bg-${type}` : ""}`}
          style={bg ? { backgroundImage: `url(${bg})` } : undefined}
        >
          <div className="announcement-scrim" />
          <div className="announcement-body">
            <div className="announcement-topbar">
              <div className="announcement-category-pill">
                <span className="announcement-announce-icon">
                  <CategoryIcon type={isSnapshotCard ? "snapshot" : type} />
                </span>
                <span className="announcement-category-text">
                  {getCategoryLabel(popup, isSnapshotCard)}
                </span>
              </div>
              <button
                type="button"
                className="announcement-close-inner"
                aria-label="Close announcement"
                onClick={dismiss}
              >
                ×
              </button>
            </div>

            <div className="announcement-main">
              {!isSnapshotCard ? (
                <h2 id="announcement-heading" className="announcement-heading">
                  {popup.heading}
                </h2>
              ) : (
                <h2 id="announcement-heading" className="sr-only">
                  Yesterday's Plant Summary
                </h2>
              )}

              {isSnapshotCard && parsedStats ? (
                <div className="snapshot-dashboard">
                  {/* Energy Consumed Hero Metric */}
                  <div className="snapshot-card snapshot-card-hero">
                    <div className="snapshot-icon-badge">⚡</div>
                    <div className="snapshot-info">
                      <span className="snapshot-label">Yesterday's Energy Consumed</span>
                      <div className="snapshot-value-row">
                        <span className="snapshot-num">
                          {parsedStats.energy_kwh?.toLocaleString()}
                        </span>
                        <span className="snapshot-unit">kWh</span>
                        <span className="snapshot-cost-tag">
                          (~PKR {parsedStats.estimated_pkr?.toLocaleString()})
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Max Demand and Avg Power Factor */}
                  <div className="snapshot-grid-two">
                    <div className="snapshot-card">
                      <div className="snapshot-icon-badge icon-mdi">📊</div>
                      <div className="snapshot-info">
                        <span className="snapshot-label">Max Demand (MDI)</span>
                        <div className="snapshot-value-row">
                          <span className="snapshot-num">
                            {parsedStats.max_demand_kw?.toLocaleString()}
                          </span>
                          <span className="snapshot-unit">kW</span>
                        </div>
                      </div>
                    </div>

                    <div className="snapshot-card">
                      <div className="snapshot-icon-badge icon-pf">🔌</div>
                      <div className="snapshot-info">
                        <span className="snapshot-label">Avg Power Factor</span>
                        <div className="snapshot-value-row">
                          <span className="snapshot-num">
                            {parsedStats.avg_power_factor}
                          </span>
                          <span
                            className={`snapshot-pf-badge pf-${parsedStats.pf_status
                              ?.toLowerCase()
                              .replace(/\s+/g, "-")}`}
                          >
                            {parsedStats.pf_status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="announcement-desc">{popup.description}</p>
              )}
            </div>

            <div className="announcement-footer">
              <div className="announcement-divider" aria-hidden />
              <div className="announcement-icons">
                {socialSlots.map((slot, idx) => {
                  const targetHref = slot.href || slot.default.href;
                  const isDefaultSvg = !slot.img;
                  return (
                    <a
                      key={idx}
                      className={`announcement-icon-link ${
                        isDefaultSvg ? slot.default.bgClass : ""
                      }`}
                      href={targetHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={slot.default.name}
                    >
                      {slot.img ? (
                        /* eslint-disable-next-line jsx-a11y/alt-text */
                        <img src={slot.img} alt={slot.default.name} />
                      ) : (
                        slot.default.iconSvg
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}



